
import React, { useState, useEffect } from 'react';
import {
    Search,
    Phone,
    Mail,
    User,
    Plus,
    Trash2,
    Edit,
} from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import AddTestPrepStudentModal from './Modals/AddTestPrepStudentModal';
import EditTestPrepStudentModal from './Modals/EditTestPrepStudentModal';

interface DbStudent {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    nationality: string;
    created_at: string;
    department?: string;
    advising_type?: string;
}

const TestPrepStudents: React.FC = () => {
    const { user } = useAuth();
    const [students, setStudents] = useState<DbStudent[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState<DbStudent | null>(null);

    useEffect(() => {
        fetchStudents();
    }, []);

    const fetchStudents = async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('testprep_students')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) {
                throw error;
            }

            setStudents(data || []);
        } catch (error: any) {
            console.error('Öğrenciler yüklenirken hata:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleStudentAdded = (newStudent: DbStudent) => {
        // Add new student to the list immediately
        setStudents([newStudent, ...students]);
        fetchStudents(); // Refresh to ensure everything is synced
    };

    const handleStudentUpdated = (updatedStudent: DbStudent) => {
        setStudents(students.map(s => s.id === updatedStudent.id ? updatedStudent : s));
        fetchStudents();
    };

    const handleDeleteStudent = async (id: string) => {
        if (!await window.appConfirm('Bu öğrenciyi silmek istediğinize emin misiniz?')) return;

        try {
            const { error } = await supabase
                .from('testprep_students')
                .delete()
                .eq('id', id);

            if (error) throw error;

            setStudents(students.filter(s => s.id !== id));
            alert('Öğrenci silindi.');
        } catch (error: any) {
            alert('Silme hatası: ' + error.message);
        }
    };

    const filteredStudents = students.filter(student =>
        student.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        TestPrep Öğrencileri
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">
                        Toplam {students.length} öğrenci
                    </p>
                </div>
                <button
                    onClick={() => setShowAddModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    Yeni Öğrenci Ekle
                </button>
            </div>

            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                        type="text"
                        placeholder="İsim, e-posta ara..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>
            </div>

            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
                <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                        <tr>
                            <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Öğrenci</th>
                            <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">İletişim</th>
                            <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Kayıt Tarihi</th>
                            <th className="text-right py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">İşlemler</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {filteredStudents.map((student) => (
                            <tr key={student.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                                <td className="py-4 px-6">
                                    <div className="flex items-center space-x-3">
                                        <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-white font-medium">
                                            {student.first_name[0]}{student.last_name[0]}
                                        </div>
                                        <div>
                                            <div className="font-medium text-gray-900 dark:text-white flex items-center gap-2 flex-wrap">
                                                <span>{student.first_name} {student.last_name}</span>
                                                {student.advising_type && (
                                                    <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded whitespace-nowrap ${
                                                        student.advising_type === 'Danışmanlık Öğrencisi'
                                                            ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                                                            : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                                                    }`}>
                                                        {student.advising_type}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-gray-500">{student.nationality}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="py-4 px-6">
                                    <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                                        <div className="flex items-center gap-2">
                                            <Mail className="w-4 h-4" /> {student.email}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Phone className="w-4 h-4" /> {student.phone}
                                        </div>
                                    </div>
                                </td>
                                <td className="py-4 px-6 text-sm text-gray-600 dark:text-gray-400">
                                    {format(new Date(student.created_at), 'dd MMM yyyy', { locale: tr })}
                                </td>
                                <td className="py-4 px-6 text-right">
                                    <button
                                        onClick={() => {
                                            setSelectedStudent(student);
                                            setShowEditModal(true);
                                        }}
                                        className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors mr-2"
                                        title="Düzenle"
                                    >
                                        <Edit className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => handleDeleteStudent(student.id)}
                                        className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                        title="Sil"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {filteredStudents.length === 0 && (
                            <tr>
                                <td colSpan={4} className="py-8 text-center text-gray-500">
                                    Kayıt bulunamadı.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <AddTestPrepStudentModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSave={handleStudentAdded}
            />

            {selectedStudent && (
                <EditTestPrepStudentModal
                    isOpen={showEditModal}
                    onClose={() => {
                        setShowEditModal(false);
                        setSelectedStudent(null);
                    }}
                    onSave={handleStudentUpdated}
                    student={selectedStudent}
                />
            )}
        </div>
    );
};

export default TestPrepStudents;
